import React from "react";
import PropTypes from "prop-types";
import Skeleton from "./Skeleton";

export default function CardSkeleton({
  variant = "stat",
  count = 1,
  className = "",
  style = {},
  children,
  ...rest
}) {
  if (count > 1) {
    return (
      <>
        {Array.from({ length: count }, (_, index) => (
          <CardSkeleton
            key={`card-skeleton-${index}`}
            variant={variant}
            className={className}
            style={style}
            {...rest}
          >
            {children}
          </CardSkeleton>
        ))}
      </>
    );
  }

  if (children) {
    return (
      <div
        className={`app-skeleton-card-panel ${className}`.trim()}
        style={style}
        aria-hidden="true"
        {...rest}
      >
        {children}
      </div>
    );
  }

  if (variant === "stat") {
    return (
      <div
        className={`app-skeleton-card-panel app-skeleton-card--stat ${className}`.trim()}
        style={style}
        aria-hidden="true"
        {...rest}
      >
        <div className="app-skeleton-card--stat-top">
          <Skeleton width="48%" height={18} radius={4} />
          <Skeleton variant="rounded" width={20} height={20} radius={5} />
        </div>
        <Skeleton width="55%" height={29} radius={6} />
        <Skeleton width="72%" height={13} radius={4} />
      </div>
    );
  }

  if (variant === "summary") {
    return (
      <div
        className={`app-skeleton-card-panel app-skeleton-card--summary ${className}`.trim()}
        style={style}
        aria-hidden="true"
        {...rest}
      >
        <Skeleton width="38%" height={12} radius={4} />
        <Skeleton width="60%" height={24} radius={6} />
        <Skeleton width="75%" height={11} radius={4} />
      </div>
    );
  }

  if (variant === "vendor" || variant === "item") {
    return (
      <div
        className={`app-skeleton-card-panel app-skeleton-card--vendor ${className}`.trim()}
        style={style}
        aria-hidden="true"
        {...rest}
      >
        <Skeleton
          className="app-skeleton-card--vendor-image"
          height={170}
          radius={8}
        />
        <div className="app-skeleton-card--vendor-content">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Skeleton width="55%" height={14} radius={4} />
            <Skeleton width="25%" height={14} radius={4} />
          </div>
          <Skeleton width="40%" height={10} radius={4} />
          <Skeleton width="90%" height={10} radius={4} />
          <div style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
            <Skeleton width="33%" height={32} radius={8} />
            <Skeleton width="33%" height={32} radius={8} />
            <Skeleton width="33%" height={32} radius={8} />
          </div>
        </div>
      </div>
    );
  }

  if (variant === "chart") {
    return (
      <div
        className={`app-skeleton-card-panel app-skeleton-card--chart ${className}`.trim()}
        style={style}
        aria-hidden="true"
        {...rest}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Skeleton width="32%" height={16} radius={4} />
          <Skeleton width="20%" height={28} radius={6} />
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            gap: "16px",
            height: "160px",
            padding: "16px 0 8px",
          }}
        >
          {[40, 75, 55, 90, 65, 80, 50, 95, 70].map((h, i) => (
            <Skeleton key={i} width="100%" height={`${h}%`} radius={6} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`app-skeleton-card-panel ${className}`.trim()}
      style={style}
      aria-hidden="true"
      {...rest}
    >
      <Skeleton width="40%" height={14} radius={4} />
      <Skeleton width="70%" height={12} radius={4} />
      <Skeleton width="55%" height={12} radius={4} />
    </div>
  );
}

CardSkeleton.propTypes = {
  variant: PropTypes.oneOf([
    "stat",
    "summary",
    "vendor",
    "item",
    "chart",
    "custom",
  ]),
  count: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.object,
  children: PropTypes.node,
};
